import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Hoodie,
  ChartLineUp,
  Camera,
  Storefront,
  Bell,
  SignOut,
  Receipt,
  User as UserIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { getNotifications, readAllNotifications } from "../lib/api";

const linkBase =
  "px-3 py-2 rounded-lg font-black uppercase tracking-wider text-xs font-display border-[3px] border-transparent hover:bg-white";
const activeCls = "border-[#0A0A0A] bg-[#FFDF85]";

export const Header = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const refreshNotifs = async () => {
    if (!user) return;
    try {
      const r = await getNotifications();
      setNotifs(r.notifications || []);
      setUnread(r.unread || 0);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    refreshNotifs();
    if (!user) return;
    const t = setInterval(refreshNotifs, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const openBell = async () => {
    setOpen((o) => !o);
    if (!open && unread > 0) {
      try {
        await readAllNotifications();
        setUnread(0);
      } catch {
        /* noop */
      }
    }
  };

  const is = (p) => pathname === p || pathname.startsWith(p + "/");

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 flex items-center justify-between gap-4 border-b-[3px] border-[#0A0A0A] bg-[#FDFBF7] px-4 py-3 sm:px-8"
    >
      <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border-[3px] border-[#0A0A0A] bg-[#D4C4FB] shadow-[3px_3px_0px_0px_#0A0A0A]">
          <Hoodie weight="duotone" size={22} />
        </div>
        <div className="hidden flex-col leading-none sm:flex">
          <span className="font-display text-xl font-black tracking-tight">ReFlip</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#4A4A4A]">
            Buy · Sell · Track
          </span>
        </div>
      </Link>

      <nav className="flex flex-1 items-center justify-center gap-1 sm:gap-2">
        <Link
          to="/"
          data-testid="nav-marketplace"
          className={`${linkBase} ${pathname === "/" ? activeCls : ""}`}
        >
          <span className="flex items-center gap-2">
            <Storefront weight="bold" size={16} />
            <span className="hidden sm:inline">Marketplace</span>
          </span>
        </Link>
        {user && (
          <>
            <Link
              to="/dashboard"
              data-testid="nav-dashboard"
              className={`${linkBase} ${is("/dashboard") ? activeCls : ""}`}
            >
              <span className="flex items-center gap-2">
                <ChartLineUp weight="bold" size={16} />
                <span className="hidden sm:inline">Dashboard</span>
              </span>
            </Link>
            <Link
              to="/scan"
              data-testid="nav-scan"
              className={`${linkBase} ${is("/scan") ? activeCls : ""}`}
            >
              <span className="flex items-center gap-2">
                <Camera weight="bold" size={16} />
                <span className="hidden sm:inline">Scan</span>
              </span>
            </Link>
            <Link
              to="/orders"
              data-testid="nav-orders"
              className={`${linkBase} ${is("/orders") ? activeCls : ""}`}
            >
              <span className="flex items-center gap-2">
                <Receipt weight="bold" size={16} />
                <span className="hidden sm:inline">Orders</span>
              </span>
            </Link>
          </>
        )}
      </nav>

      <div className="flex items-center gap-2">
        {user ? (
          <>
            <div className="relative">
              <button
                data-testid="notif-button"
                onClick={openBell}
                className="relative rounded-lg border-[3px] border-[#0A0A0A] bg-white p-2 shadow-[3px_3px_0_0_#0A0A0A] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#0A0A0A]"
              >
                <Bell weight="bold" size={18} />
                {unread > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full border-[2px] border-[#0A0A0A] bg-[#FFA0A0] px-1 text-[10px] font-black"
                  >
                    {unread}
                  </span>
                )}
              </button>
              {open && (
                <div
                  data-testid="notif-popover"
                  className="absolute right-0 top-12 z-50 max-h-96 w-80 overflow-y-auto rounded-xl border-[3px] border-[#0A0A0A] bg-white p-2 shadow-[6px_6px_0_0_#0A0A0A]"
                >
                  {notifs.length === 0 ? (
                    <div className="p-3 text-sm font-medium text-[#4A4A4A]">No notifications yet.</div>
                  ) : (
                    notifs.map((n) => (
                      <div
                        key={n.id}
                        className={`mb-2 rounded-lg border-[2px] border-[#0A0A0A] p-3 text-sm ${
                          n.type === "sale"
                            ? "bg-[#B4F0A7]"
                            : n.type === "purchase"
                              ? "bg-[#D4C4FB]"
                              : n.type === "stock"
                                ? "bg-[#FFA0A0]"
                                : "bg-[#FFDF85]"
                        }`}
                      >
                        <div className="font-display text-sm font-black">{n.title}</div>
                        <div className="mt-1 text-xs font-medium">{n.body}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <Link
              to="/settings"
              data-testid="nav-settings"
              className="hidden items-center gap-2 rounded-lg border-[3px] border-[#0A0A0A] bg-white px-3 py-2 shadow-[3px_3px_0_0_#0A0A0A] hover:bg-[#FDFBF7] sm:flex"
              title="Account settings"
            >
              <UserIcon weight="duotone" size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">{user.name}</span>
            </Link>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="nb-btn nb-btn-ghost flex items-center gap-2 px-3 py-2"
              title="Log out"
            >
              <SignOut weight="bold" size={16} />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </>
        ) : (
          <>
            <Link to="/login" data-testid="nav-login" className={`${linkBase} hover:bg-white`}>
              Log in
            </Link>
            <Link to="/register" data-testid="nav-register" className="nb-btn">
              Sign up
            </Link>
          </>
        )}
      </div>
    </header>
  );
};
